import {
  OnboardingStatusValue,
  TwilioOnboardingStatus,
} from '../../types/twilio-onboarding';

// --- Wizard step logic ---

export const WIZARD_STEPS: { title: string }[] = [
  { title: 'Business Info' },
  { title: 'Authorized Rep' },
  { title: 'Campaign Config' },
  { title: 'Compliance Check' },
  { title: 'Review & Preview' },
  { title: 'Submit' },
];

export const TOTAL_STEPS = WIZARD_STEPS.length;

export const STEP_FIELD_GROUPS: Record<number, string[][]> = {
  0: [
    ['business_info', 'legal_entity_name'],
    ['business_info', 'brand_name'],
    ['business_info', 'business_type'],
    ['business_info', 'ein'],
    ['business_info', 'business_website'],
    ['business_info', 'street_address'],
    ['business_info', 'city'],
    ['business_info', 'state'],
    ['business_info', 'postal_code'],
  ],
  1: [
    ['authorized_rep', 'first_name'],
    ['authorized_rep', 'last_name'],
    ['authorized_rep', 'email'],
    ['authorized_rep', 'phone'],
    ['authorized_rep', 'business_title'],
    ['authorized_rep', 'job_position'],
  ],
  2: [
    ['campaign_info', 'client_website'],
    ['campaign_info', 'form_url'],
    ['campaign_info', 'form_button_text'],
    ['campaign_info', 'listing_url'],
    ['campaign_info', 'privacy_policy_url'],
    ['campaign_info', 'terms_url'],
    ['campaign_info', 'assistant_name'],
    ['campaign_info', 'team_name'],
    ['area_code'],
  ],
};

export function stepRequiresValidation(step: number): boolean {
  return step >= 0 && step <= 2;
}

export function getFieldsForStep(step: number): string[][] | null {
  return STEP_FIELD_GROUPS[step] ?? null;
}

export function computeNextStep(current: number): number {
  return Math.min(current + 1, TOTAL_STEPS - 1);
}

export function computePrevStep(current: number): number {
  return Math.max(current - 1, 0);
}

export function canNavigateBackward(
  targetStep: number,
  currentStep: number,
): boolean {
  return targetStep < currentStep;
}

export function getNextButtonLabel(currentStep: number): string {
  if (currentStep >= TOTAL_STEPS - 1) {
    return '';
  }
  return currentStep >= 3 ? 'Skip / Next' : 'Next';
}

// --- Onboarding status / state-machine logic ---

export const STATUS_STEPS: { key: OnboardingStatusValue; label: string }[] = [
  { key: 'pending_profile', label: 'Profile Review' },
  { key: 'pending_brand', label: 'Brand Registration' },
  { key: 'pending_campaign', label: 'Campaign Review' },
  { key: 'pending_number', label: 'Number Provisioning' },
  { key: 'active', label: 'Active' },
];

export const STATUS_STEP_INDEX: Record<string, number> = {
  pending_profile: 0,
  profile: 0,
  pending_brand: 1,
  brand: 1,
  pending_campaign: 2,
  campaign: 2,
  pending_number: 3,
  number: 3,
  active: 4,
};

export type StepDisplayStatus = 'finish' | 'process' | 'wait' | 'error';

export function resolveCurrentStepIndex(
  status: TwilioOnboardingStatus,
): number {
  if (status.status === 'rejected') {
    return STATUS_STEP_INDEX[status.rejection_step ?? 'pending_profile'] ?? 0;
  }
  return STATUS_STEP_INDEX[status.status] ?? 0;
}

export function getStepDisplayStatus(
  stepIndex: number,
  status: TwilioOnboardingStatus,
): StepDisplayStatus {
  const isRejected = status.status === 'rejected';
  const currentIndex = resolveCurrentStepIndex(status);

  if (isRejected && stepIndex === currentIndex) {
    return 'error';
  }
  if (stepIndex < currentIndex) {
    return 'finish';
  }
  if (stepIndex === currentIndex) {
    return status.status === 'active' ? 'finish' : 'process';
  }
  return 'wait';
}

export function getAllStepDisplayStatuses(
  status: TwilioOnboardingStatus,
): StepDisplayStatus[] {
  return STATUS_STEPS.map((_, i) => getStepDisplayStatus(i, status));
}

export function determinePageMode(
  status: TwilioOnboardingStatus | null,
): 'setup' | 'monitor' {
  return status ? 'monitor' : 'setup';
}

export function shouldPollStatus(status: TwilioOnboardingStatus): boolean {
  return status.status !== 'active' && status.status !== 'rejected';
}

export function getStatusTagColor(status: OnboardingStatusValue): string {
  const colors: Record<OnboardingStatusValue, string> = {
    pending_profile: 'processing',
    pending_brand: 'processing',
    pending_campaign: 'orange',
    pending_number: 'cyan',
    active: 'green',
    rejected: 'red',
  };
  return colors[status] ?? 'default';
}

/**
 * A validator for URL fields that accepts URLs with or without a protocol.
 */
export const urlValidator = (_: any, value: string) => {
  if (!value) {
    return Promise.resolve();
  }
  let testUrl = value;
  if (!/^https?:\/\//i.test(value)) {
    testUrl = 'https://' + value;
  }
  try {
    const url = new URL(testUrl);
    if (!url.hostname.includes('.')) {
      return Promise.reject(new Error('Please enter a valid URL'));
    }
    return Promise.resolve();
  } catch {
    return Promise.reject(new Error('Please enter a valid URL'));
  }
};

/**
 * Normalizes a URL to ensure it has a protocol (defaulting to https://).
 */
export function ensureProtocol(url: string | undefined | null): string {
  if (!url) {
    return '';
  }
  const trimmed = url.trim();
  if (!trimmed) {
    return '';
  }
  if (!/^https?:\/\//i.test(trimmed)) {
    return `https://${trimmed}`;
  }
  return trimmed;
}

/**
 * Normalizes a US phone number to E.164 (+1XXXXXXXXXX) when possible.
 * Falls back to the original input if it doesn't look like a 10/11-digit
 * US number, leaving the field's required pattern to flag it.
 */
export function formatPhoneE164(raw: string | undefined | null): string {
  if (!raw) {
    return '';
  }
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  return raw;
}

// --- Business name validation (Twilio Trust Hub 64-char limit) ---

// The onboarding pipeline composes the Twilio customer name as
// `<legal entity> DBA <brand>` (AgentAIAssistantAPI service.py), and Twilio's
// Address `customer_name` field hard-rejects anything over 64 chars. A too-long
// name only surfaces as an HTTP 400 mid-pipeline, leaving the record stuck at
// `pending_profile`, so we validate it in the form instead.
export const TWILIO_CUSTOMER_NAME_MAX_LENGTH = 64;

// Matches "dba", "d/b/a", "d.b.a" as a standalone token (not letters embedded in
// a longer word like "Goodbar" or "EMBA"), case-insensitive.
const DBA_TOKEN = /(?:^|[^a-zA-Z])(dba|d[./]b[./]a)(?:[^a-zA-Z]|$)/i;

export function composeTwilioCustomerName(
  legalEntityName: string,
  brandName: string,
): string {
  return `${legalEntityName} DBA ${brandName}`;
}

// A DBA / trade name belongs in the Brand Name field. Keeping it out of the legal
// entity name avoids a redundant, over-length composed customer name (the exact
// failure seen on client 580: "... dba Aspyre Collective DBA Aspyre Collective").
export function getLegalEntityNameError(
  value: string | undefined,
): string | null {
  if (!value) {
    return null;
  }
  if (DBA_TOKEN.test(value)) {
    return 'Enter only the registered legal entity name (e.g. "Acme Realty LLC"). Put any DBA / trade name in the Brand Name field below.';
  }
  return null;
}

// The legal entity and brand names share the 64-char budget of the composed
// `<legal> DBA <brand>` customer name.
export function getBusinessNameLengthError(
  legalEntityName: string | undefined,
  brandName: string | undefined,
): string | null {
  if (!legalEntityName || !brandName) {
    return null;
  }
  const composed = composeTwilioCustomerName(legalEntityName, brandName);
  if (composed.length > TWILIO_CUSTOMER_NAME_MAX_LENGTH) {
    return `Legal entity and brand names are too long together — ${composed.length} characters, but Twilio allows at most ${TWILIO_CUSTOMER_NAME_MAX_LENGTH}. Shorten the legal or brand name.`;
  }
  return null;
}
